import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { catchError, EMPTY, Observable } from 'rxjs'
import { LogService } from 'htv-lib'
import { RegisterDto, RegisterResponseDto } from '../interfaces/dto/register-dto'
import { StatusResponseDto } from '../interfaces/dto/status-response-dto'
import { environment } from '../environments/environment'
import { MonitoringDto } from '../interfaces/dto/monitoring-dto'
import { EpgChannelDataDto } from '../interfaces/dto/epg-dto'
import { MonitoringService } from './monitoring.service'
import { MonitoringResponseDto } from '../interfaces/dto/monitoring-response-dto'
import { NetworkService } from './network.service'
import { MediaService } from './media.service'
import { SmartAppService } from './smart-app.service'
import { HttpUtil } from '../utils/http-util'
import { CastService } from './cast.service'
import { StateDataService } from './state-data.service'

@Injectable({
  providedIn: 'root',
})
export class HumecloudService {
  readonly DEVICE_API_URL = environment.deviceApiUrl
  readonly HTV_API_URL = environment.htvApiUrl
  readonly CMN_LOCATION_API_URL = environment.cmnLocationApiUrl

  constructor(
    private castService: CastService,
    private httpClient: HttpClient,
    private logService: LogService,
    private mediaService: MediaService,
    private monitoringService: MonitoringService,
    private networkService: NetworkService,
    private smartAppService: SmartAppService,
    private stateDataService: StateDataService
  ) {
    // do nothing
  }

  postRegister(code?: string, roomNumber?: string): Observable<RegisterResponseDto> {
    const systemData = this.monitoringService.getSystemData()
    const registerDto: RegisterDto = {
      code: code,
      roomNumber: roomNumber,
      model: systemData.model,
      manufacturer: systemData.manufacturer,
      serialNumber: systemData.serialNumber,
      macAddresses: [this.networkService.getMACAddress()],
      ipAddresses: [this.networkService.getIPAddress()],
      firmwareVersion: systemData.firmwareVersion,
      appVersion: systemData.appVersion,
    }
    const url = `${this.DEVICE_API_URL}/register`
    return this.httpClient.post<RegisterResponseDto>(url, registerDto).pipe(
      catchError(err => {
        this.logService.error(`Could not retrieve register from Humecloud, error = ${err?.message}`)
        throw err
      })
    )
  }

  getStatus(): Observable<StatusResponseDto> {
    const url = `${this.DEVICE_API_URL}/status`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))
    return this.httpClient.get<StatusResponseDto>(url, { headers }).pipe(
      catchError(err => {
        this.logService.error(`Could not retrieve status from Humecloud, error = ${err?.message}`)
        throw err
      })
    )
  }

  postMonitoring(): Observable<MonitoringResponseDto> {
    const systemData = this.monitoringService.getSystemData()
    const monitoringData = this.monitoringService.getMonitoringData()
    const platformData = this.monitoringService.getPlatformData()
    const networkData = this.networkService.getNetworkData()
    const medias = this.mediaService.getReport()
    const gcState = this.castService.gcState
    const localApps = this.smartAppService.localApps
    const data = JSON.stringify({
      system: systemData,
      monitoring: monitoringData,
      network: networkData,
      platform: platformData,
      googlecast: gcState,
      localApps: localApps,
    })
    const monitoringDto: MonitoringDto = {
      model: systemData.model,
      manufacturer: systemData.manufacturer,
      serialNumber: systemData.serialNumber,
      macAddresses: networkData ? [networkData.macAddress] : [],
      ipAddresses: networkData ? [networkData.ipAddress] : [],
      firmwareVersion: systemData.firmwareVersion,
      appVersion: systemData.appVersion,
      data: data,
      assets: medias,
      network: networkData,
      googlecast: gcState,
      localApps: localApps,
    }
    const url = `${this.DEVICE_API_URL}/monitoring`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))
    return this.httpClient.post<MonitoringResponseDto>(url, monitoringDto, { headers }).pipe(
      catchError(err => {
        this.logService.error(`Could not send monitoring to Humecloud, error = ${err?.message}`)
        return EMPTY
      })
    )
  }

  postLogs(formData: FormData): Observable<any> {
    const url = `${this.DEVICE_API_URL}/logs`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))
    return this.httpClient.post<any>(url, formData, { headers }).pipe(
      catchError(err => {
        this.logService.error(`Could not send logs to Humecloud, error = ${err?.message}`)
        return EMPTY
      })
    )
  }

  getEpgData(
    globalChannelIds: string[],
    start: Date,
    end: Date,
    langs: string[] = []
  ): Observable<EpgChannelDataDto[]> {
    const url = `${this.HTV_API_URL}/public/epg`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))
    let queryParams = new HttpParams().set('start', start.toISOString()).set('end', end.toISOString())
    if (langs) {
      langs.forEach(lang => {
        queryParams = queryParams.append('langs', lang)
      })
    }
    return this.httpClient.post<EpgChannelDataDto[]>(url, globalChannelIds, { headers, params: queryParams }).pipe(
      catchError(err => {
        this.logService.error(`Could not retrieve EPG data from Humecloud, error = ${err?.message}`)
        throw err
      })
    )
  }
}
